/* 5S Workplace Audit Wizard with domain-specific & customisable questionnaire. */
import { useEffect, useState } from "react";
import ToolWizard from "./ToolWizard";
import api from "../../utils/api";
import { Plus, Trash2, RefreshCw } from "lucide-react";

const PILLARS = [
  { key: "sort", name: "Sort (Seiri)" },
  { key: "set", name: "Set in Order (Seiton)" },
  { key: "shine", name: "Shine (Seiso)" },
  { key: "standardise", name: "Standardise (Seiketsu)" },
  { key: "sustain", name: "Sustain (Shitsuke)" },
];

function pillarScore(items, scores) {
  const total = items.length * 5;
  const sum = (scores || []).reduce((a, s) => a + (Number(s) || 0), 0);
  return { sum, total, pct: total > 0 ? (sum / total) * 100 : 0 };
}

function overallScore(data) {
  const totals = PILLARS.map(p => {
    const items = data?.questionnaire?.[p.key] || [];
    const scores = data?.pillars?.[p.key]?.scores || [];
    return pillarScore(items, scores);
  });
  const sum = totals.reduce((a, t) => a + t.sum, 0);
  const max = totals.reduce((a, t) => a + t.total, 0);
  return { sum, max, pct: max > 0 ? (sum / max) * 100 : 0, byPillar: totals };
}

function PillarEditor({ pillarKey, pillarName, data, updateData, fivesTemplates }) {
  const items = data?.questionnaire?.[pillarKey] || [];
  const entry = data?.pillars?.[pillarKey] || { scores: items.map(() => 0), notes: "" };
  const [newItem, setNewItem] = useState("");

  const setItems = (newItems) => {
    const newScores = newItems.map((_, i) => entry.scores?.[i] || 0);
    updateData({
      questionnaire: { ...(data.questionnaire || {}), [pillarKey]: newItems },
      pillars: { ...(data.pillars || {}), [pillarKey]: { ...entry, scores: newScores } },
    });
  };
  const setScore = (i, v) => {
    const scores = [...(entry.scores || items.map(() => 0))];
    scores[i] = v;
    updateData({ pillars: { ...(data.pillars || {}), [pillarKey]: { ...entry, scores } } });
  };
  const setNotes = (v) => updateData({ pillars: { ...(data.pillars || {}), [pillarKey]: { ...entry, notes: v } } });
  const updateItemText = (i, text) => setItems(items.map((it, idx) => idx === i ? text : it));
  const addItem = () => {
    if (!newItem.trim()) return;
    setItems([...items, newItem.trim()]);
    setNewItem("");
  };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const resetFromDomain = () => {
    const tmpl = fivesTemplates?.[data.domain] || fivesTemplates?.["Other / Generic"];
    if (tmpl?.[pillarKey]) setItems(tmpl[pillarKey]);
  };

  const score = pillarScore(items, entry.scores);

  return (
    <div data-testid={`fives-pillar-${pillarKey}`}>
      <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-sage-600">Rate each item on 0–5. <strong>Score: {score.sum}/{score.total} ({score.pct.toFixed(0)}%)</strong></p>
        <button onClick={resetFromDomain} className="flex items-center gap-1 text-xs border border-sage-200 rounded-md px-2 py-1 text-sage-700 hover:bg-sage-50" data-testid={`reset-${pillarKey}`}><RefreshCw className="w-3 h-3" /> Reset to {data.domain || "default"} template</button>
      </div>

      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 bg-sage-50 rounded-md p-3" data-testid={`fives-item-${pillarKey}-${i}`}>
            <input value={item} onChange={(e) => updateItemText(i, e.target.value)} className="flex-1 bg-transparent text-sm text-sage-800 outline-none border-b border-transparent hover:border-sage-200 focus:border-sage-400 py-0.5" />
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setScore(i, n)} className={`w-7 h-7 rounded text-xs font-semibold ${(entry.scores?.[i] || 0) === n ? "bg-sage-600 text-white" : "bg-white border border-sage-200 text-sage-600 hover:bg-sage-100"}`}>{n}</button>
              ))}
            </div>
            <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-3">
        <input data-testid={`add-item-input-${pillarKey}`} placeholder="Add custom audit item…" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} className="flex-1 border border-sage-200 rounded-md px-3 py-2 text-sm" />
        <button data-testid={`add-item-${pillarKey}`} onClick={addItem} className="flex items-center gap-1 bg-sage-600 text-white rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-700"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>

      <textarea placeholder="Observations & remediation notes" value={entry.notes || ""} onChange={e => setNotes(e.target.value)} className="mt-3 w-full border border-sage-200 rounded-md px-3 py-2 text-sm min-h-[80px]" data-testid={`fives-notes-${pillarKey}`} />
    </div>
  );
}

export default function FiveSWizard({ project, company, branding, onSave, onExportPDF }) {
  const [fivesTemplates, setFivesTemplates] = useState({});

  useEffect(() => {
    api.get("/domains").then(r => setFivesTemplates(r.data.fives_templates || {})).catch(() => {});
  }, []);

  // Initial seed: if no questionnaire yet, fill from company.domain template
  const initialData = project?.tool_data || {};
  if (!initialData.questionnaire && fivesTemplates && Object.keys(fivesTemplates).length > 0) {
    const dom = company?.domain || "Other / Generic";
    const tmpl = fivesTemplates[dom] || fivesTemplates["Other / Generic"];
    if (tmpl) {
      initialData.questionnaire = tmpl;
      initialData.domain = dom;
    }
  }
  const seededProject = { ...project, tool_data: initialData };

  const steps = [
    {
      key: "meta", label: "Audit meta",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Audit area / site</label><input data-testid="fives-area" value={data.area || ""} onChange={e => updateData({ area: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" placeholder="e.g. Floor 2, West Wing" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Auditor</label><input value={data.auditor || ""} onChange={e => updateData({ auditor: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Audit date</label><input type="date" value={data.audit_date || ""} onChange={e => updateData({ audit_date: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Target score (%)</label><input type="number" value={data.target_pct || 80} onChange={e => updateData({ target_pct: Number(e.target.value) })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-sage-600 mb-1">Domain (defines questionnaire template)</label>
            <div className="flex gap-2">
              <select data-testid="fives-domain-select" value={data.domain || ""} onChange={e => {
                const newDomain = e.target.value;
                const tmpl = fivesTemplates[newDomain] || fivesTemplates["Other / Generic"];
                updateData({ domain: newDomain, questionnaire: tmpl });
              }} className="flex-1 border border-sage-200 rounded-md px-3 py-2 text-sm bg-white">
                {Object.keys(fivesTemplates).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <button data-testid="apply-domain-template" onClick={() => {
                const tmpl = fivesTemplates[data.domain] || fivesTemplates["Other / Generic"];
                updateData({ questionnaire: tmpl });
              }} className="border border-sage-200 text-sage-700 rounded-md px-3 py-2 text-xs font-semibold hover:bg-sage-50 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Apply template</button>
            </div>
            <p className="text-xs text-sage-500 mt-1">Pre-seeded from company domain. You can edit, add or remove items per pillar in the next steps.</p>
          </div>
        </div>
      ),
    },
    ...PILLARS.map(p => ({
      key: p.key, label: p.name,
      render: ({ data, updateData }) => <PillarEditor pillarKey={p.key} pillarName={p.name} data={data} updateData={updateData} fivesTemplates={fivesTemplates} />,
    })),
    {
      key: "summary", label: "Summary",
      render: ({ data }) => {
        const s = overallScore(data);
        const target = data.target_pct || 80;
        const pass = s.pct >= target;
        return (
          <div className="space-y-4" data-testid="fives-summary">
            <div className={`rounded-md p-4 ${pass ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
              <p className="text-xs uppercase tracking-wider font-semibold mb-1">{pass ? "Audit passed" : "Below target"}</p>
              <p className="font-heading text-2xl font-bold">{s.pct.toFixed(1)}% <span className="text-sm font-normal">({s.sum}/{s.max})</span></p>
              <p className="text-xs mt-0.5">Target: {target}% · Domain template: {data.domain || "—"}</p>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {PILLARS.map((p, i) => (
                <div key={p.key} className="bg-white border border-sage-100 rounded-md p-3 text-center">
                  <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">{p.name.split(" ")[0]}</p>
                  <p className="font-heading text-lg font-bold text-sage-900 mt-1">{s.byPillar[i].pct.toFixed(0)}%</p>
                  <p className="text-xs text-sage-400">{s.byPillar[i].sum}/{s.byPillar[i].total}</p>
                </div>
              ))}
            </div>
            <button data-testid="export-5s-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700">Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={seededProject} steps={steps} onSave={onSave} completedLabel="Complete audit" />;
}
